import { pickT } from "@repo/shared";
import type { FlowField, Translated } from "@repo/shared/types";
import { Copy, GripVertical, Pencil, Settings, Trash2 } from "lucide-react";
import React, { useState } from "react";
import {
  getFlowComponentLabel,
  getFlowComponentRowLabel,
} from "@/components/flow/flow-component-types";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import type { FlowPageComponent, TabConfig } from "@/lib/types/flow";
import { getComponentIcon } from "../../../../shared/component-icons";
import { T } from "./tabs-block.i18n";

// Move ComponentItem OUTSIDE to prevent recreation on every render
const ComponentItem = React.memo(
  ({
    component,
    tabId,
    index,
    draggedComponentRef,
    draggedItem,
    disabled,
    flowLanguage,
    onDragStart,
    onDragEnd,
    onDrop,
    onComponentSettings,
    onComponentDelete,
    onComponentDuplicate,
    getComponentLabel,
    hasConditionalDisplay,
    translations,
  }: {
    component: FlowPageComponent & { flowField?: FlowField };
    tabId: string;
    index: number;
    draggedComponentRef: React.RefObject<string | null>;
    draggedItem: any;
    disabled?: boolean;
    flowLanguage: string;
    onDragStart: (e: React.DragEvent, componentId: string, tabId: string, index: number) => void;
    onDragEnd: () => void;
    onDrop: (e: React.DragEvent, targetTabId: string, targetIndex: number) => void;
    onComponentSettings: (component: FlowPageComponent & { flowField?: FlowField }) => void;
    onComponentDelete: (component: FlowPageComponent & { flowField?: FlowField }) => void;
    onComponentDuplicate: (component: FlowPageComponent & { flowField?: FlowField }) => void;
    getComponentLabel: (component: FlowPageComponent & { flowField?: FlowField }) => string;
    hasConditionalDisplay: (component: FlowPageComponent) => boolean;
    translations: Translated<typeof T>;
  }) => {
    const [isDropTarget, setIsDropTarget] = useState(false);
    const dragHandleRef = React.useRef<HTMLDivElement>(null);

    // Check if this component is being dragged using the ref
    const isDragging = draggedComponentRef.current === component.id;

    return (
      // biome-ignore lint/a11y/noStaticElementInteractions: drag-and-drop requires interactive div
      <div
        key={component.id}
        className={`
        group relative
        flex items-center gap-3 p-3 border rounded-lg
        bg-background hover:bg-accent/50
        transition-all
        ${isDragging ? "opacity-30" : ""}
        ${isDropTarget && !isDragging ? "border-primary bg-primary/5 border-2" : ""}
      `}
        onDragEnter={(e) => {
          e.preventDefault();
          // Don't allow layout blocks to be dropped into tabs
          const isLayoutBlock =
            draggedItem?.type === "page-item" && draggedItem?.itemType === "layout";
          if (draggedItem && !isDragging && !isLayoutBlock) {
            setIsDropTarget(true);
          }
        }}
        onDragOver={(e) => {
          // Don't allow layout blocks to be dropped here
          const isLayoutBlock =
            draggedItem?.type === "page-item" && draggedItem?.itemType === "layout";
          if (isLayoutBlock) {
            e.dataTransfer.dropEffect = "none";
            return;
          }
          e.preventDefault();
          e.stopPropagation();
          e.dataTransfer.dropEffect = "move";
        }}
        onDragLeave={(e) => {
          // Only clear if we're leaving the entire component
          if (!e.currentTarget.contains(e.relatedTarget as Node)) {
            setIsDropTarget(false);
          }
        }}
        onDrop={(e) => {
          e.preventDefault();
          e.stopPropagation();
          setIsDropTarget(false);
          // Don't allow layout blocks to be dropped into tabs
          const isLayoutBlock =
            draggedItem?.type === "page-item" && draggedItem?.itemType === "layout";
          if (isLayoutBlock) {
            return;
          }
          onDrop(e, tabId, index);
        }}
      >
        {/* Separate drag handle that's always draggable */}
        {/* biome-ignore lint/a11y/noStaticElementInteractions: drag handle requires drag events */}
        <div
          ref={dragHandleRef}
          draggable={true}
          onDragStart={(e) => {
            if (disabled) {
              e.preventDefault();
              return;
            }
            e.stopPropagation();
            onDragStart(e, component.id, tabId, index);
          }}
          onDragEnd={(e) => {
            e.stopPropagation();
            onDragEnd();
          }}
          className="cursor-move p-1 -m-1 hover:bg-accent rounded"
        >
          <GripVertical className="h-5 w-5 text-muted-foreground" />
        </div>
        {(() => {
          const ComponentIcon = getComponentIcon(component.componentType);
          return (
            <ComponentIcon className="h-5 w-5 text-muted-foreground flex-shrink-0 pointer-events-none" />
          );
        })()}

        <div className="flex-1 min-w-0 pointer-events-none">
          <div className="flex items-center gap-2">
            <Badge variant="outline" className="text-xs flex-shrink-0">
              {getFlowComponentLabel(component.componentType, flowLanguage)}
            </Badge>
            <span className="font-medium truncate">{getComponentLabel(component)}</span>
            {component.componentType === "field" && component.flowField?.isRequired && (
              <Badge variant="destructive" className="text-xs">
                {translations.required}
              </Badge>
            )}
            {hasConditionalDisplay(component) && (
              <Badge variant="secondary" className="text-xs">
                {translations.conditional}
              </Badge>
            )}
          </div>
        </div>

        <div
          className={`flex gap-1 flex-shrink-0 ${draggedItem ? "pointer-events-none" : "pointer-events-auto"}`}
        >
          <Button
            variant="ghost"
            size="icon"
            tooltip={translations.settings}
            onClick={(e) => {
              e.stopPropagation();
              onComponentSettings(component);
            }}
            disabled={!!draggedItem}
          >
            <Settings className="h-4 w-4" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            tooltip={translations.duplicateComponent}
            onClick={(e) => {
              e.stopPropagation();
              onComponentDuplicate(component);
            }}
            disabled={!!draggedItem}
          >
            <Copy className="h-4 w-4" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            tooltip={translations.deleteComponent}
            onClick={(e) => {
              e.stopPropagation();
              onComponentDelete(component);
            }}
            disabled={!!draggedItem}
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      </div>
    );
  },
);

ComponentItem.displayName = "ComponentItem";

type TabsBlockProps = {
  layoutId: string;
  layoutConfig?: string | null; // JSON configuration including conditional display and tabs
  components: (FlowPageComponent & { flowField?: FlowField })[];
  flowLanguage: string;
  onDelete: () => void;
  onLayoutSettings: () => void;
  onTabSettings: (tabId: string) => void;
  onAddToTab: (tabId: string) => void;
  onAddTab: () => void;
  onDeleteTab: (tabId: string) => void;
  onRenameTab: (tabId: string, newName: string) => void;
  onComponentSettings: (component: FlowPageComponent & { flowField?: FlowField }) => void;
  onComponentDelete: (component: FlowPageComponent & { flowField?: FlowField }) => void;
  onComponentDuplicate: (component: FlowPageComponent & { flowField?: FlowField }) => void;
  onReorderTab: (tabId: string, componentOrders: { id: string; order: number }[]) => void;
  onMoveComponentBetweenTabs: (componentId: string, toTabId: string, toOrder: number) => void;
  onDragStart: (componentId: string, tabId: string) => void;
  onDragEnd: () => void;
  onLayoutDragStart?: () => void;
  onLayoutDrop?: () => void;
  draggedItem: {
    id: string;
    type: "page-item" | "layout-component" | "tabs-component";
    sourceLayoutId?: string;
    sourceTabId?: string;
    itemType?: "layout" | "component";
  } | null;
  disabled?: boolean;
};

export function TabsBlock({
  layoutId,
  layoutConfig,
  components,
  flowLanguage,
  onDelete,
  onLayoutSettings,
  onTabSettings,
  onAddToTab,
  onAddTab,
  onDeleteTab,
  onRenameTab,
  onComponentSettings,
  onComponentDelete,
  onComponentDuplicate,
  onReorderTab,
  onMoveComponentBetweenTabs,
  onDragStart,
  onDragEnd,
  onLayoutDragStart,
  onLayoutDrop,
  draggedItem,
  disabled,
}: TabsBlockProps) {
  const t = pickT(T, flowLanguage);

  // Local state for drag feedback and tracking
  const [localDragState, setLocalDragState] = useState<{
    componentId: string;
    tabId: string;
    index: number;
  } | null>(null);

  // Track which tab name is being edited
  const [editingTabId, setEditingTabId] = useState<string | null>(null);
  const [editingTabName, setEditingTabName] = useState("");

  // Use a ref to track which component is being dragged to avoid re-renders
  const draggedComponentRef = React.useRef<string | null>(null);

  // Parse tabs from layout config
  const tabs: TabConfig[] = React.useMemo(() => {
    try {
      const config = layoutConfig ? JSON.parse(layoutConfig) : null;
      return config?.tabs || [];
    } catch {
      return [];
    }
  }, [layoutConfig]);

  // Check if layout has conditional display
  const hasLayoutConditionalDisplay = () => {
    try {
      const config = layoutConfig ? JSON.parse(layoutConfig) : null;
      return config?.conditionalDisplay?.enabled === true;
    } catch {
      return false;
    }
  };

  // Check if a tab has description configured
  const hasTabSettings = (tabId: string) => {
    const tab = tabs.find((t) => t.id === tabId);
    if (!tab) return false;
    return !!tab.description;
  };

  // Get components for a specific tab
  const getTabComponents = (tabId: string) => {
    return components.filter((c) => c.tabId === tabId).sort((a, b) => a.order - b.order);
  };

  const getComponentLabel = (component: FlowPageComponent & { flowField?: FlowField }) =>
    getFlowComponentRowLabel(component, flowLanguage);

  const hasConditionalDisplay = (component: FlowPageComponent) => {
    try {
      const config = component.config ? JSON.parse(component.config) : null;
      return config?.conditionalDisplay?.enabled === true;
    } catch {
      return false;
    }
  };

  // Drag handlers
  const handleDragStart = (
    e: React.DragEvent,
    componentId: string,
    tabId: string,
    index: number,
  ) => {
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/plain", componentId);

    // Create custom drag preview
    const dragPreview = document.createElement("div");
    dragPreview.style.cssText = `
      position: fixed;
      top: -9999px;
      left: -9999px;
      width: 400px;
      height: 60px;
      background: rgba(59, 130, 246, 0.2);
      border: 2px dashed rgb(59, 130, 246);
      border-radius: 8px;
      padding: 16px;
      font-size: 14px;
      font-weight: 500;
      color: rgb(59, 130, 246);
      display: flex;
      align-items: center;
      justify-content: center;
    `;
    dragPreview.textContent = t.movingComponent;
    document.body.appendChild(dragPreview);
    e.dataTransfer.setDragImage(dragPreview, 20, 30);

    // Clean up after next frame
    requestAnimationFrame(() => {
      document.body.removeChild(dragPreview);
    });

    // Store in ref to avoid re-renders
    draggedComponentRef.current = componentId;
    setLocalDragState({ componentId, tabId, index });

    // Delay parent state update
    setTimeout(() => {
      onDragStart(componentId, tabId);
    }, 0);
  };

  const handleDragEnd = () => {
    draggedComponentRef.current = null;
    setLocalDragState(null);
    onDragEnd();
  };

  const handleTabDrop = (e: React.DragEvent, targetTabId: string, targetIndex: number) => {
    e.preventDefault();
    e.stopPropagation();

    if (!draggedItem) {
      return;
    }

    // Handle page-item (standalone component) being dropped into tab
    if (draggedItem.type === "page-item" && draggedItem.itemType === "component") {
      onMoveComponentBetweenTabs(draggedItem.id, targetTabId, targetIndex);
      return;
    }

    // Handle tabs-component drops
    if (draggedItem.type === "tabs-component") {
      // Check if this is same-tab reordering
      if (
        localDragState &&
        draggedItem.sourceLayoutId === layoutId &&
        localDragState.tabId === targetTabId
      ) {
        // Same tab reordering - use onReorderTab
        const sourceIndex = localDragState.index;

        // Don't reorder if dropping on itself
        if (sourceIndex === targetIndex) {
          return;
        }

        const tabComponents = getTabComponents(targetTabId);
        const sourceComponents = [...tabComponents];
        const [movedComponent] = sourceComponents.splice(sourceIndex, 1);

        // Adjust target index if moving forward
        const adjustedTargetIndex = sourceIndex < targetIndex ? targetIndex - 1 : targetIndex;
        sourceComponents.splice(adjustedTargetIndex, 0, movedComponent);

        const componentOrders = sourceComponents.map((component, index) => ({
          id: component.id,
          order: index,
        }));

        onReorderTab(targetTabId, componentOrders);
      } else {
        // Cross-tab or cross-layout move
        onMoveComponentBetweenTabs(draggedItem.id, targetTabId, targetIndex);
      }
    }

    // Handle layout-component drops (from 2-column layout)
    if (draggedItem.type === "layout-component") {
      onMoveComponentBetweenTabs(draggedItem.id, targetTabId, targetIndex);
    }
  };

  const startEditingTab = (tabId: string, currentName: string) => {
    setEditingTabId(tabId);
    setEditingTabName(currentName);
  };

  const saveTabName = () => {
    if (editingTabId && editingTabName.trim()) {
      onRenameTab(editingTabId, editingTabName.trim());
    }
    setEditingTabId(null);
    setEditingTabName("");
  };

  return (
    <Card className="p-4">
      {/* Header with drag handle */}
      {/* biome-ignore lint/a11y/noStaticElementInteractions: drag-and-drop requires interactive div */}
      <div
        className="flex items-center justify-between mb-4"
        onDragOver={(e) => {
          if (
            (draggedItem?.type === "page-item" && draggedItem?.id !== layoutId) ||
            draggedItem?.type === "tabs-component" ||
            draggedItem?.type === "layout-component"
          ) {
            e.preventDefault();
            e.dataTransfer.dropEffect = "move";
            e.currentTarget.classList.add("ring-2", "ring-primary", "rounded");
          }
        }}
        onDragLeave={(e) => {
          e.currentTarget.classList.remove("ring-2", "ring-primary", "rounded");
        }}
        onDrop={(e) => {
          e.preventDefault();
          e.currentTarget.classList.remove("ring-2", "ring-primary", "rounded");

          if (draggedItem?.type === "page-item" && draggedItem?.id !== layoutId) {
            onLayoutDrop?.();
          } else if (
            draggedItem?.type === "tabs-component" ||
            draggedItem?.type === "layout-component"
          ) {
            onLayoutDrop?.();
          }
        }}
      >
        <div className="flex items-center gap-2">
          {/* biome-ignore lint/a11y/noStaticElementInteractions: drag handle requires drag events */}
          <div
            className="cursor-move p-1 hover:bg-accent rounded"
            draggable={!disabled}
            onDragStart={(e) => {
              const layoutElement = (e.currentTarget as HTMLElement).closest(
                "[data-tabs-block-container]",
              ) as HTMLElement;
              const elementWidth = layoutElement ? layoutElement.offsetWidth : 800;

              const dragPreview = document.createElement("div");
              dragPreview.style.cssText = `
                position: fixed;
                top: -9999px;
                left: -9999px;
                width: ${elementWidth}px;
                height: 120px;
                background: rgba(59, 130, 246, 0.2);
                border: 2px dashed rgb(59, 130, 246);
                border-radius: 8px;
                padding: 16px;
              `;
              dragPreview.innerHTML = `
                <div style="display: flex; gap: 8px; height: 100%;">
                  <div style="background: rgba(59, 130, 246, 0.3); border-radius: 4px; border: 1px dashed rgb(59, 130, 246); flex: 1;"></div>
                  <div style="background: rgba(59, 130, 246, 0.2); border-radius: 4px; border: 1px dashed rgb(59, 130, 246); flex: 1;"></div>
                  <div style="background: rgba(59, 130, 246, 0.2); border-radius: 4px; border: 1px dashed rgb(59, 130, 246); flex: 1;"></div>
                </div>
              `;
              document.body.appendChild(dragPreview);
              e.dataTransfer.setDragImage(dragPreview, 20, 60);

              requestAnimationFrame(() => {
                document.body.removeChild(dragPreview);
              });

              e.stopPropagation();
              onLayoutDragStart?.();
            }}
            onDragEnd={(e) => {
              e.stopPropagation();
              onDragEnd();
            }}
          >
            <GripVertical className="h-5 w-5 text-muted-foreground" />
          </div>
          <Badge variant="secondary">{t.tabsBlock}</Badge>
          {hasLayoutConditionalDisplay() && (
            <Badge variant="secondary" className="text-xs">
              {t.conditional}
            </Badge>
          )}
        </div>
        <div className="flex gap-1">
          <Button
            size="icon"
            tooltip={t.settings}
            variant="ghost"
            onClick={onLayoutSettings}
            disabled={disabled}
          >
            <Settings className="h-4 w-4" />
          </Button>
          <Button
            size="icon"
            tooltip={t.deleteTabsBlock}
            variant="ghost"
            onClick={onDelete}
            disabled={disabled}
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Tabs - displayed vertically */}
      <div className="space-y-4">
        {tabs.map((tab) => {
          const tabComponents = getTabComponents(tab.id);

          return (
            <div key={tab.id} className="border-2 border-dashed rounded-lg p-3">
              {/* Tab header */}
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  {editingTabId === tab.id ? (
                    <Input
                      value={editingTabName}
                      onChange={(e) => setEditingTabName(e.target.value)}
                      onBlur={saveTabName}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") saveTabName();
                        if (e.key === "Escape") {
                          setEditingTabId(null);
                          setEditingTabName("");
                        }
                      }}
                      className="h-6 w-32 text-xs"
                      autoFocus
                    />
                  ) : (
                    <>
                      <span className="text-sm font-medium">{tab.name}</span>
                      <Button
                        variant="ghost"
                        size="icon"
                        tooltip={t.edit}
                        className="h-5 w-5 p-0"
                        onClick={() => startEditingTab(tab.id, tab.name)}
                        disabled={disabled}
                      >
                        <Pencil className="h-3 w-3" />
                      </Button>
                    </>
                  )}
                  {hasTabSettings(tab.id) && (
                    <Badge variant="secondary" className="text-[10px] px-1 py-0">
                      {t.desc}
                    </Badge>
                  )}
                  {tab.conditionalDisplay?.enabled === true && (
                    <Badge variant="secondary" className="text-xs">
                      {t.conditional}
                    </Badge>
                  )}
                </div>
                <div className="flex items-center gap-1">
                  <Button
                    size="icon"
                    tooltip={t.settings}
                    variant="ghost"
                    onClick={() => onTabSettings(tab.id)}
                    disabled={disabled}
                    className="h-6 w-6 p-0"
                  >
                    <Settings className="h-3 w-3" />
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => onAddToTab(tab.id)}
                    disabled={disabled}
                    className="h-6 text-xs gap-1"
                  >
                    {t.addComponent}
                  </Button>
                  {tabs.length > 1 && (
                    <Button
                      size="icon"
                      tooltip={t.deleteTab}
                      variant="ghost"
                      onClick={() => onDeleteTab(tab.id)}
                      disabled={disabled}
                      className="h-6 w-6 p-0 text-destructive hover:text-destructive"
                    >
                      <Trash2 className="h-3 w-3" />
                    </Button>
                  )}
                </div>
              </div>

              {/* Tab components */}
              <div className="space-y-2">
                {tabComponents.length === 0 ? (
                  // biome-ignore lint/a11y/noStaticElementInteractions: drop zone requires drag events
                  <div
                    onDragOver={(e) => {
                      const isLayoutBlock =
                        draggedItem?.type === "page-item" && draggedItem?.itemType === "layout";
                      if (isLayoutBlock) {
                        return;
                      }
                      e.preventDefault();
                      if (draggedItem) {
                        e.currentTarget.classList.add("border-primary", "bg-primary/5");
                      }
                    }}
                    onDragLeave={(e) => {
                      e.currentTarget.classList.remove("border-primary", "bg-primary/5");
                    }}
                    onDrop={(e) => {
                      e.preventDefault();
                      e.currentTarget.classList.remove("border-primary", "bg-primary/5");
                      const isLayoutBlock =
                        draggedItem?.type === "page-item" && draggedItem?.itemType === "layout";
                      if (isLayoutBlock) {
                        return;
                      }
                      if (draggedItem) {
                        handleTabDrop(e, tab.id, 0);
                      }
                    }}
                    className="text-center py-8 text-xs text-muted-foreground border-2 border-dashed rounded-lg transition-all"
                  >
                    {draggedItem &&
                    !(draggedItem.type === "page-item" && draggedItem.itemType === "layout")
                      ? t.dropComponentHere
                      : t.noComponents}
                  </div>
                ) : (
                  tabComponents.map((component, index) => (
                    <ComponentItem
                      key={component.id}
                      component={component}
                      tabId={tab.id}
                      index={index}
                      draggedComponentRef={draggedComponentRef}
                      draggedItem={draggedItem}
                      disabled={disabled}
                      flowLanguage={flowLanguage}
                      onDragStart={handleDragStart}
                      onDragEnd={handleDragEnd}
                      onDrop={handleTabDrop}
                      onComponentSettings={onComponentSettings}
                      onComponentDelete={onComponentDelete}
                      onComponentDuplicate={onComponentDuplicate}
                      getComponentLabel={getComponentLabel}
                      hasConditionalDisplay={hasConditionalDisplay}
                      translations={t}
                    />
                  ))
                )}
              </div>
            </div>
          );
        })}

        {/* Add tab button */}
        <Button
          variant="outline"
          size="sm"
          onClick={onAddTab}
          disabled={disabled}
          className="w-full"
        >
          {t.addTab}
        </Button>
      </div>
    </Card>
  );
}
